import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  TicketIcon, Network, Plus, LogOut, User, Clock, CheckCircle2,
  AlertCircle, Loader2, Building2, Wifi, Wallet, Tv, Train, AlertTriangle,
  ChevronRight, RotateCcw,
} from 'lucide-react';
import CustomerSwitcher, { useActiveCustomer } from '@/components/CustomerSwitcher';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [services, setServices] = useState({ customers: [], circuits: [] });
  const [tickets, setTickets] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [iptvBalance, setIptvBalance] = useState(0);
  const [hasIptvWallet, setHasIptvWallet] = useState(false);
  const [railtelBalance, setRailtelBalance] = useState(0);
  const [hasRailtelWallet, setHasRailtelWallet] = useState(false);
  const [railtelEnabled, setRailtelEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPartner, setIsPartner] = useState(false);
  const [isLco, setIsLco] = useState(false);
  const [hasIPTV, setHasIPTV] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [expiryStats, setExpiryStats] = useState({
    iptv: { expired: 0, expiring_in_2d: 0, expiring_in_3d: 0, total_active: 0 },
    ott:  { expired: 0, expiring_in_2d: 0, expiring_in_3d: 0, total_active: 0 },
    railtel: { expired: 0, expiring_in_2d: 0, expiring_in_3d: 0, total_active: 0 },
  });

  // Drill-down dialog
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillItems, setDrillItems] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  // Top-up popup
  const [topUp, setTopUp] = useState(null); // { type: 'ott'|'iptv'|'railtel', amount, loading }
  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';
  const { activeId, setActiveId } = useActiveCustomer(services.customers);

  const TOPUP_META = {
    ott: { label: 'OTT Wallet', color: 'emerald' },
    iptv: { label: 'IPTV Wallet', color: 'indigo' },
    railtel: { label: 'ANT+RAILTEL Wallet', color: 'orange' },
  };

  const getActiveCustomerId = () =>
    activeId || localStorage.getItem('portal_active_customer_id') || services.customers?.[0]?.customer_id;

  const handleTopUpPay = async () => {
    const amt = parseFloat(topUp?.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    const cid = getActiveCustomerId();
    setTopUp(prev => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem('portal_token');
      let url, options;
      if (topUp.type === 'iptv') {
        url = `${API}/api/portal/iptv/wallet/topup`;
        options = { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: amt }) };
      } else if (topUp.type === 'railtel') {
        url = `${API}/api/railtel/portal/wallet/topup/initiate`;
        options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customer_id: cid, amount: amt }) };
      } else {
        url = `${API}/api/wallet/portal/topup/initiate`;
        options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customer_id: cid, amount: amt }) };
      }
      const res = await fetch(url, options);
      const data = await res.json();
      if (res.ok && data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        toast.error((typeof data.detail === 'string' ? data.detail : data.message) || 'Payment initiation failed');
        setTopUp(prev => ({ ...prev, loading: false }));
      }
    } catch {
      toast.error('Connection error. Please try again.');
      setTopUp(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('portal_user');
    const token = localStorage.getItem('portal_token');
    if (!storedUser || !token) {
      navigate(basePath);
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchData(token);
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (token) => {
    try {
      if (!token) token = localStorage.getItem('portal_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch services and tickets in parallel
      const [servicesRes, ticketsRes] = await Promise.all([
        fetch(`${API}/api/portal/my-services`, { headers }),
        fetch(`${API}/api/portal/tickets`, { headers })
      ]);

      if (servicesRes.status === 401 || ticketsRes.status === 401) {
        handleLogout();
        return;
      }

      const servicesData = await servicesRes.json();
      const ticketsData = await ticketsRes.json();

      setServices(servicesData);
      setTickets(ticketsData.tickets || []);

      // Check if any linked customer is a partner with LCO management enabled
      const partnerCustomer = servicesData.customers?.find(c => c.customer_type === 'partner' && c.lco_management_enabled);
      setIsPartner(!!partnerCustomer);

      // Check if logged-in customer is an LCO (has parent_partner_id)
      const lcoCustomer = servicesData.customers?.find(c => c.parent_partner_id);
      setIsLco(!!lcoCustomer);

      // Check if partner OR bulk_bandwidth customer has IPTV access
      const iptvPartner = servicesData.customers?.find(c =>
        (c.customer_type === 'partner' || c.customer_type === 'bulk_bandwidth') && c.iptv_access_token
      );
      setHasIPTV(!!iptvPartner);

      // Get customer/partner name for welcome message — use the active (switched) customer
      const storedActive = localStorage.getItem('portal_active_customer_id');
      const pickedCustomer =
        (storedActive && servicesData.customers?.find(c => c.customer_id === storedActive)) ||
        servicesData.customers?.[0];
      if (pickedCustomer) {
        setCustomerName(pickedCustomer.company_name || pickedCustomer.contact_person || pickedCustomer.contact_name || '');
        setRailtelEnabled(!!pickedCustomer.railtel_enabled);
      }

      // Fetch wallet balance(s) if customer linked — always for the active one
      if (pickedCustomer?.customer_id) {
        const cid = pickedCustomer.customer_id;
        try {
          const wRes = await fetch(`${API}/api/wallet/portal/balance?customer_id=${cid}`);
          if (wRes.ok) {
            const wData = await wRes.json();
            setWalletBalance(wData.balance || 0);
          }
        } catch (err) {
          console.error('Failed to fetch wallet balance:', err);
        }
        try {
          const iRes = await fetch(`${API}/api/iptv/portal/balance?customer_id=${cid}`);
          if (iRes.ok) {
            const iData = await iRes.json();
            setIptvBalance(iData.balance || 0);
            // Show IPTV section if there's any IPTV activity (balance > 0)
            // OR if the customer is an LCO/Partner that's expected to have IPTV
            setHasIptvWallet((iData.balance || 0) > 0);
          }
        } catch (err) {
          console.error('Failed to fetch IPTV wallet balance:', err);
        }
        try {
          const rtRes = await fetch(`${API}/api/railtel/portal/balance?customer_id=${cid}`);
          if (rtRes.ok) {
            const rtData = await rtRes.json();
            setRailtelBalance(rtData.balance || 0);
            setHasRailtelWallet((rtData.balance || 0) > 0);
          }
        } catch (err) {
          console.error('Failed to fetch Railtel wallet balance:', err);
        }
      }

      // Expiry stats (best-effort)
      if (activeId || pickedCustomer?.customer_id) {
        try {
          const cid = activeId || pickedCustomer.customer_id;
          const sRes = await fetch(`${API}/api/portal/expiry-stats?customer_id=${cid}`, { headers });
          if (sRes.ok) {
            const sData = await sRes.json();
            setExpiryStats(sData);
          }
        } catch (err) {
          console.error('Failed to fetch expiry stats:', err);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    navigate(basePath);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      pending_customer: 'bg-purple-100 text-purple-700',
      resolved: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600'
    };
    return <Badge className={styles[priority] || 'bg-slate-100'}>{priority}</Badge>;
  };

  const openDrill = async (service, bucket) => {
    const cid = activeId;
    if (!cid) return;
    setDrillLoading(true);
    setDrillItems([]);
    const labels = { expired: 'Expired', '2d': 'Expiring in ≤ 2 days', '3d': 'Expiring in ≤ 3 days' };
    const sLabels = { iptv: 'IPTV', ott: 'OTT', railtel: 'ANT+RAILTEL' };
    setDrillTitle(`${sLabels[service]} — ${labels[bucket]}`);
    setDrillOpen(true);
    try {
      const tk = localStorage.getItem('portal_token');
      const r = await fetch(`${API}/api/portal/expiry-stats/list?service=${service}&bucket=${bucket}&customer_id=${cid}`, {
        headers: { 'Authorization': `Bearer ${tk}` },
      });
      const d = await r.json();
      setDrillItems(d.items || []);
    } catch { toast.error('Failed to load list'); }
    setDrillLoading(false);
  };

  const handleRechargeNow = (item) => {
    setDrillOpen(false);
    const rechargeState = {
      recharge: {
        mobile: item.mobile || '',
        name: item.subscriber_name || item.name || '',
        planId: item.current_plan_id || '',
        planName: item.package || '',
        subscriptionId: item.subscriber_id || '',
      },
    };
    if (item.service === 'iptv') navigate(`${basePath}/iptv`, { state: rechargeState });
    else if (item.service === 'ott') navigate(`${basePath}/wallet`, { state: rechargeState });
    else if (item.service === 'railtel') navigate(`${basePath}/railtel`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const openTickets = tickets.filter(t => !['resolved', 'closed'].includes(t.status));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="ANT Broadband" className="h-9 w-auto" />
            <div>
              <h1 className="font-bold text-lg text-slate-900 tracking-tight">
                {customerName ? `Hello, ${customerName}` : 'Customer Portal'}
              </h1>
              <p className="text-xs text-slate-500">Manage all your services in one place</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="portal-logout-btn">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        {services.customers?.length > 1 && (
          <div className="max-w-4xl mx-auto px-4 pb-2 flex justify-end">
            <CustomerSwitcher customers={services.customers} activeId={activeId} onChange={setActiveId} />
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Active Services</p>
                  <p className="text-2xl font-bold">{services.circuits.length}</p>
                </div>
                <Wifi className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Open Tickets</p>
                  <p className="text-2xl font-bold">{openTickets.length}</p>
                </div>
                <TicketIcon className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3">
          <Button 
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => navigate(`${basePath}/tickets/new`)}
          >
            <Plus className="mr-2 h-4 w-4" /> Raise Ticket
          </Button>
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(`${basePath}/wallet`)}
            data-testid="portal-wallet-btn"
          >
            <Wallet className="mr-2 h-4 w-4" /> OTT
          </Button>
          {isPartner && (
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => navigate(`${basePath}/lcos`)}
              data-testid="portal-lcos-btn"
            >
              <User className="mr-2 h-4 w-4" /> Manage LCOs
            </Button>
          )}
          {isPartner && (
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => navigate(`${basePath}/commissions`)}
              data-testid="portal-commissions-btn"
            >
              <Wallet className="mr-2 h-4 w-4" /> Commissions
            </Button>
          )}
          {(isPartner || hasIPTV || isLco || hasIptvWallet) && (
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => navigate(`${basePath}/iptv`)}
              data-testid="portal-iptv-btn"
            >
              <Tv className="mr-2 h-4 w-4" /> IPTV
            </Button>
          )}
          {(hasRailtelWallet || railtelEnabled) && (
            <Button 
              variant="outline" 
              className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => navigate(`${basePath}/railtel`)}
              data-testid="portal-railtel-btn"
            >
              <Train className="mr-2 h-4 w-4" /> ANT+RAILTEL
            </Button>
          )}
        </div>

        {/* Wallet Cards — OTT + IPTV side-by-side (when IPTV applies) */}
        <div className={`grid gap-3 ${(isLco || hasIptvWallet || hasIPTV || isPartner) ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="portal-ott-wallet-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> OTT Wallet</p>
                  <p className="text-2xl font-bold" data-testid="dashboard-wallet-balance">
                    Rs.{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <Button 
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => setTopUp({ type: 'ott', amount: '' })}
                  data-testid="dashboard-topup-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Top Up
                </Button>
              </div>
            </CardContent>
          </Card>
          {(isLco || hasIptvWallet || hasIPTV || isPartner) && (
            <Card className="bg-gradient-to-br from-indigo-900 to-indigo-800 text-white" data-testid="portal-iptv-wallet-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-indigo-200 text-sm flex items-center gap-1"><Tv className="h-3.5 w-3.5" /> IPTV Wallet</p>
                    <p className="text-2xl font-bold" data-testid="dashboard-iptv-balance">
                      Rs.{iptvBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <Button 
                    size="sm"
                    className="bg-indigo-500 hover:bg-indigo-600"
                    onClick={() => setTopUp({ type: 'iptv', amount: '' })}
                    data-testid="dashboard-iptv-topup-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Top Up
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {(hasRailtelWallet || railtelEnabled) && (
            <Card className="bg-gradient-to-br from-orange-600 to-amber-600 text-white" data-testid="portal-railtel-wallet-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm flex items-center gap-1">
                      <Wallet className="h-3.5 w-3.5" /> ANT+RAILTEL Wallet
                    </p>
                    <p className="text-2xl font-bold" data-testid="dashboard-railtel-balance">
                      Rs.{railtelBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-orange-500 hover:bg-orange-700"
                    onClick={() => setTopUp({ type: 'railtel', amount: '' })}
                    data-testid="dashboard-railtel-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Top Up
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* My Services */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Network className="h-5 w-5" /> My Services
            </CardTitle>
            <CardDescription>Your active circuits and connections</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {services.circuits.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <Wifi className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                <p>No services linked to this number</p>
                <p className="text-xs mt-1">Contact support if you have active services</p>
              </div>
            ) : (
              <div className="divide-y">
                {services.circuits.map((circuit) => (
                  <div key={circuit.circuit_id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{circuit.location_name}</p>
                        <p className="text-sm text-slate-500">{circuit.ant_circuit_id}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">{circuit.bandwidth}</Badge>
                          <span className="text-xs text-slate-400">{circuit.city}</span>
                        </div>
                      </div>
                      <Badge className={circuit.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                        {circuit.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Expiry Stats — shown for any customer who has at
            least one tracked subscription OR a service wallet (so they get
            a "✓ healthy" empty-state instead of nothing). */}
        {(() => {
          const totalExpired = (expiryStats.iptv?.expired || 0) + (expiryStats.ott?.expired || 0) + (expiryStats.railtel?.expired || 0);
          const total2d = (expiryStats.iptv?.expiring_in_2d || 0) + (expiryStats.ott?.expiring_in_2d || 0) + (expiryStats.railtel?.expiring_in_2d || 0);
          const total3d = (expiryStats.iptv?.expiring_in_3d || 0) + (expiryStats.ott?.expiring_in_3d || 0) + (expiryStats.railtel?.expiring_in_3d || 0);
          const totalActive = (expiryStats.iptv?.total_active || 0) + (expiryStats.ott?.total_active || 0) + (expiryStats.railtel?.total_active || 0);
          const hasAnyService = hasIPTV || hasIptvWallet || hasRailtelWallet || railtelEnabled || isPartner || isLco || totalActive > 0 || totalExpired > 0;
          if (!hasAnyService) return null;
          const isAllClear = totalActive === 0 && totalExpired === 0 && total2d === 0 && total3d === 0;
          return (
            <Card data-testid="portal-expiry-stats-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" /> Subscription Expiry Watchlist
                </CardTitle>
                <CardDescription>
                  Daily WhatsApp reminders are sent at 9 AM &amp; 6 PM IST, 1 day before expiry.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {isAllClear ? (
                  <div className="flex items-center gap-3 px-3 py-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="expiry-all-clear">
                    <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-emerald-700">All clear — no upcoming expiries</p>
                      <p className="text-xs text-emerald-600">When any of your IPTV / OTT / ANT+RAILTEL subscriptions get within 3 days of expiry, they will appear here. Daily WhatsApp reminders also go out at 9 AM &amp; 6 PM IST.</p>
                    </div>
                  </div>
                ) : (
                <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="expiry-stat-expired">
                    <p className="text-xs uppercase tracking-wide text-red-700">Expired</p>
                    <p className="text-2xl font-bold text-red-700">{totalExpired}</p>
                  </div>
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="expiry-stat-2d">
                    <p className="text-xs uppercase tracking-wide text-amber-700">Expiring ≤ 2 days</p>
                    <p className="text-2xl font-bold text-amber-700">{total2d}</p>
                  </div>
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-3" data-testid="expiry-stat-3d">
                    <p className="text-xs uppercase tracking-wide text-orange-700">Expiring ≤ 3 days</p>
                    <p className="text-2xl font-bold text-orange-700">{total3d}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-1 border-t">
                  {[
                    { key: 'iptv', label: 'IPTV', color: 'text-indigo-700', icon: Tv },
                    { key: 'ott', label: 'OTT', color: 'text-emerald-700', icon: Wifi },
                    { key: 'railtel', label: 'ANT+RAILTEL', color: 'text-orange-700', icon: Train },
                  ].map(({ key, label, color, icon: Icon }) => {
                    const s = expiryStats[key] || {};
                    const Row = ({ bucketKey, value, cls }) => (
                      <button
                        type="button"
                        disabled={!value}
                        onClick={() => openDrill(key, bucketKey)}
                        className={`w-full flex items-center justify-between text-left px-2 py-1 rounded transition ${value ? `${cls} hover:bg-slate-100 cursor-pointer` : 'text-slate-400'}`}
                        data-testid={`expiry-drill-${key}-${bucketKey}`}
                      >
                        <span>{bucketKey === 'expired' ? 'Expired' : `≤ ${bucketKey}`}</span>
                        <span className="font-medium flex items-center gap-0.5">
                          {value}{value > 0 && <ChevronRight className="h-3 w-3" />}
                        </span>
                      </button>
                    );
                    return (
                      <div key={key} className="text-xs space-y-0.5" data-testid={`expiry-stat-${key}`}>
                        <p className={`font-semibold flex items-center gap-1 ${color} mb-1`}><Icon className="h-3.5 w-3.5" /> {label}</p>
                        <div className="px-2 py-1 flex justify-between"><span>Active</span><span className="font-medium">{s.total_active || 0}</span></div>
                        <Row bucketKey="expired" value={s.expired || 0} cls="text-red-600" />
                        <Row bucketKey="2d" value={s.expiring_in_2d || 0} cls="text-amber-600" />
                        <Row bucketKey="3d" value={s.expiring_in_3d || 0} cls="text-orange-600" />
                      </div>
                    );
                  })}
                </div>
                </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Recent Tickets */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <TicketIcon className="h-5 w-5" /> Recent Tickets
                </CardTitle>
                <CardDescription>Your support requests</CardDescription>
              </div>
              {tickets.length > 0 && (
                <Link to="/portal/tickets" className="text-sm text-emerald-600 hover:underline">
                  View all
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {tickets.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <TicketIcon className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                <p>No tickets yet</p>
                <Button 
                  variant="link" 
                  className="mt-2 text-emerald-600"
                  onClick={() => navigate(`${basePath}/tickets/new`)}
                >
                  Raise your first ticket
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {tickets.slice(0, 5).map((ticket) => (
                  <Link 
                    key={ticket.ticket_id} 
                    to={`/portal/tickets/${ticket.ticket_id}`}
                    className="block p-4 hover:bg-slate-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-400">{ticket.ticket_number}</span>
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        <p className="font-medium text-slate-900 truncate">{ticket.subject}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(ticket.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {getStatusBadge(ticket.status)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Companies */}
        {services.customers.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Linked Accounts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {services.customers.map((customer) => (
                  <div key={customer.customer_id} className="p-4">
                    <p className="font-medium">{customer.company_name}</p>
                    <p className="text-sm text-slate-500">{customer.customer_code}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col" data-testid="expiry-drill-dialog">
          <DialogHeader>
            <DialogTitle>{drillTitle}</DialogTitle>
            <DialogDescription>
              {drillItems.length} subscriber{drillItems.length === 1 ? '' : 's'}. Click "Recharge Now" to jump to the recharge screen.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {drillLoading ? (
              <div className="py-12 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            ) : drillItems.length === 0 ? (
              <div className="py-12 text-center text-slate-400">No subscribers in this bucket.</div>
            ) : (
              <div className="divide-y border rounded">
                {drillItems.map((it, i) => (
                  <div key={i} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`drill-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{it.name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {it.mobile && <span>{it.mobile} • </span>}
                        {it.username && <span>{it.username} • </span>}
                        {it.package && <span>{it.package} • </span>}
                        ends {it.end_date}
                      </p>
                    </div>
                    <Badge className={it.days_to_expiry < 0 ? 'bg-red-100 text-red-700' : it.days_to_expiry <= 1 ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'}>
                      {it.days_to_expiry < 0 ? `${-it.days_to_expiry}d ago` : `${it.days_to_expiry}d left`}
                    </Badge>
                    <Button size="sm" onClick={() => handleRechargeNow(it)} data-testid={`drill-recharge-${i}`} className="bg-indigo-600 hover:bg-indigo-700">
                      <RotateCcw className="h-3 w-3 mr-1" /> Recharge Now
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrillOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top-Up Popup (OTT / IPTV / Railtel) */}
      <Dialog open={!!topUp} onOpenChange={(o) => { if (!o) setTopUp(null); }}>
        <DialogContent className="max-w-sm" data-testid="dashboard-topup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" /> Top Up {topUp && TOPUP_META[topUp.type]?.label}
            </DialogTitle>
            <DialogDescription>Enter an amount and pay securely via Easebuzz.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="flex flex-wrap gap-2">
              {[100, 200, 500, 1000, 2000].map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setTopUp(prev => ({ ...prev, amount: String(v) }))}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${String(topUp?.amount) === String(v) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
                  data-testid={`topup-chip-${v}`}
                >
                  ₹{v}
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm text-slate-600">Amount (₹)</label>
              <Input
                type="number" min="1"
                value={topUp?.amount || ''}
                onChange={(e) => setTopUp(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
                data-testid="topup-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopUp(null)} data-testid="topup-cancel">Cancel</Button>
            <Button onClick={handleTopUpPay} disabled={topUp?.loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="topup-pay-btn">
              {topUp?.loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Pay Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
